<template>
  <div>
    <div v-if="sent">
      <component :is="resultComponent" @reset="resetForm" @close="onClose" />
    </div>

    <vee-form
      v-else
      ref="widgetForm"
      v-slot="{ meta }"
      class="tw-px-16 tw-py-12"
      @submit="onSubmit"
    >
      <h1 class="tw-text-3xl tw-font-semibold tw-leading-9 tw-text-center tw-mb-8">
        {{ $t('widget.title') }}
      </h1>
      <vee-field v-slot="{ field, errors, handleChange }" rules="required|max:32" name="name">
        <dp-input
          :model-value="field.value"
          size="lg"
          :label="$t('widget.form.first_name')"
          :validation="buildValidationResult(errors)"
          @update:model-value="handleChange"
        />
      </vee-field>
      <vee-field v-slot="{ field, errors, handleChange }" rules="required|max:32" name="surname">
        <dp-input
          :model-value="field.value"
          size="lg"
          :label="$t('widget.form.last_name')"
          :validation="buildValidationResult(errors)"
          @update:model-value="handleChange"
        />
      </vee-field>
      <vee-field
        v-slot="{ field, errors, handleChange }"
        rules="required|valid_phone_number"
        name="phone"
      >
        <dp-input
          :model-value="field.value"
          size="lg"
          :label="$t('widget.form.phone_number')"
          :validation="buildValidationResult(errors)"
          @update:model-value="handleChange"
        />
      </vee-field>
      <vee-field v-slot="{ field, errors, handleChange }" rules="required" name="content">
        <dp-textarea
          :model-value="field.value"
          rows="4"
          :label="$t('widget.form.message')"
          :validation="buildValidationResult(errors)"
          @update:model-value="handleChange"
        />
      </vee-field>

      <div class="tw-flex tw-justify-center tw-mt-5">
        <dp-button
          type="submit"
          color="primary"
          size="lg"
          :state="!meta.valid || meta.pending ? 'disabled' : null"
        >
          {{ $t('button.send_message') }}
        </dp-button>

        <dp-button type="button" size="lg" class="tw-ml-3" @click="onClose">
          {{ $t('button.cancel') }}
        </dp-button>
      </div>

      <footer class="tw-mt-5">
        <p
          v-sanitize-html="displayedFooterHtml"
          class="footer tw-text-xs tw-text-center tw-text-gray-normal"
        />
      </footer>
    </vee-form>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

import { Form as VeeForm, Field as VeeField } from 'vee-validate';
import { DpInput, DpTextarea, DpButton } from '@dp-ui-kit/vue';
import { toNative } from 'vue-facing-decorator';

import { ValidationMixin as validationMixin } from '@/mixins/validation';
import { getEndpointConfig } from '@/utils/endpointDetector';
import FormWidgetSuccess from '@/components/form-widget/FormWidgetSuccess.vue';
import FormWidgetFailure from '@/components/form-widget/FormWidgetFailure.vue';
import { textOnly } from '@/utils/sanitize-html';

const { apiUrl } = getEndpointConfig();

export default defineComponent({
  name: 'FormWidget',

  components: {
    VeeForm,
    VeeField,
    DpInput,
    DpTextarea,
    DpButton,
  },

  mixins: [toNative(validationMixin)],

  props: {
    id: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      sent: false,
      success: null,
      companyName: '',
      privacyPolicyUrl: '',
    };
  },

  computed: {
    resultComponent() {
      return this.success ? FormWidgetSuccess : FormWidgetFailure;
    },

    displayedFooterHtml() {
      const htmlContent = this.$t('widget.footer_with_link', {
        companyName: this.companyName,
      });

      return htmlContent.replace('href="#"', `href="${this.privacyPolicyUrl}"`);
    },
  },

  beforeCreate() {
    document.body.classList.add('dp-widget');
  },

  created() {
    window.addEventListener('message', this.onMessage);
    window.addEventListener('resize', this.postIframeHeightToParent);
    this.getFooterDetails();
  },

  mounted() {
    this.$nextTick(this.postIframeHeightToParent);
  },

  beforeUnmount() {
    window.removeEventListener('message', this.onMessage);
    window.removeEventListener('resize', this.postIframeHeightToParent);
    document.body.classList.remove('dp-widget');
  },

  methods: {
    onMessage({ data }) {
      switch (data?.name) {
        case 'com.docplanner.phone#form-widget:reset': {
          this.resetForm();
          break;
        }
        case 'com.docplanner.phone#set-footer': {
          this.setFooter(data);
          break;
        }
        default:
          break;
      }
    },

    resetForm() {
      if (this.$refs.widgetForm) {
        this.$refs.widgetForm.resetForm();
      }

      this.sent = false;
    },

    onClose() {
      this.resetForm();
      this.postMessageToParentWindow({ message: 'com.docplanner.phone#modal:close' });
    },

    async onSubmit(data) {
      try {
        const response = await fetch(`https://${apiUrl}/contact-message/${this.id}.json`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }).then(res => res.json());

        this.success = response.message === 'success';
        this.sent = true;
      } catch {
        this.success = false;
        this.sent = true;
      }
    },

    postMessageToParentWindow(message) {
      const parentWindow = window.parent;

      // iframe detection
      if (!parentWindow || parentWindow === window) {
        return;
      }

      parentWindow.postMessage(message, '*');
    },

    postIframeHeightToParent() {
      const message = {
        message: 'com.docplanner.phone#iframe-height',
        height: document.getElementById('app').offsetHeight,
      };
      this.postMessageToParentWindow(message);
    },

    setFooter({ companyName, privacyPolicyUrl }) {
      this.privacyPolicyUrl = textOnly(privacyPolicyUrl);
      this.companyName = textOnly(companyName);
    },

    getFooterDetails() {
      this.postMessageToParentWindow({ message: 'com.docplanner.phone#get-footer' });
    },
  },
});
</script>

<style lang="scss" scoped>
@use 'src/assets/scss/variables' as v;

.footer :deep(a) {
  font-size: v.$font-size-sm;
  color: v.$primary;

  &:hover {
    text-decoration: underline;
  }
}
</style>
